<template>
  <div>
    <div v-if="surveysAreLoading">
      <loading></loading>
    </div>
    <div v-else-if="surveysHaveLoaded">
      <div class="centered-row regular-text">
        <div class="outer-container">
          <div class="line-spacing primary-color bold font-secondary">
            {{ getQuestionNumber }}/{{ totalAmountOfQuestions }}
          </div>
          <div class="line-spacing regular-text">{{ getRatingQuestion }}</div>
          <div v-if="isGeneralBrandSurvey">
            <div class="label-spacing" v-for="(ratingLabel, idx) in ratingLabels" :key="idx">
              {{ ratingLabel }}
            </div>
          </div>
          <div class="answers line-spacing">
            <div class="extra-padding">
              <div class="each-answer" v-for="(answer, idx) in answers" :key="idx">
                <span>{{ answer }}</span>
              </div>
            </div>
          </div>
          <div class="entire-ratings-component line-spacing">
            <div>
              <div class="rating-container" v-for="(rating, idx) in ratings" :key="idx">
                <div
                  class="rating-box"
                  v-bind:class="indicateDisabled"
                  @click="ratingClick(rating)"
                >
                  {{ rating }}
                </div>
              </div>
            </div>
          </div>
          <div v-if="isProspx">
            <div class="label-spacing" v-for="(ratingLabel, idx) in ratingLabels" :key="idx">
              {{ ratingLabel }}
            </div>
          </div>
          <FooterBar></FooterBar>
        </div>
      </div>
    </div>
    <div class="centered-row" v-else-if="surveyLoadingError">
      <ErrorPage>
        <div slot="header">{{ surveyNotLoading }}</div>
        <div slot="message">{{ surveyNotLoadingMessage }}</div>
      </ErrorPage>
    </div>
  </div>
</template>

<script>
import http from "../../services/axios";
import {
  COMPLETE_SURVEY,
  GET_BACKEND_URL,
  GET_HTTP_CONFIG,
  LOADING_COMPLETED_FAILURE,
  LOADING_COMPLETED_SUCCESS,
  LOADING_STARTED,
  SET_USER_POSITION_IN_SURVEY,
  USER_POSITION_IN_SURVEY,
} from "../../services/constants";
import FooterBar from "../views/FooterBar";
import ErrorPage from "../views/ErrorPage";

export default {
  name: "SurveyMediaQuestions",
  props: {
    mediaQuestions: {
      type: Array,
      default: () => [],
    },
    currentQuestion: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      studyInfo: "",
      ratingQuestion: {},
      answers: [],
      maxRating: 0,
      participantID: 0,
      disableClick: false,
      start: new Date(),
      surveyLoadingStatus: LOADING_STARTED,
    };
  },

  components: {
    ErrorPage,
    FooterBar,
  },

  methods: {
    setAnswers() {},
    updateSurvey() {
      this.setAnswers();
      this.start = new Date();
    },
    setComponentState(response) {
      const questions = this.mediaQuestions;
      let preview = this.$store.state.surveyModule.isSurveyPreview;
      this.ratingQuestions = questions;
      this.maxRating = 5;
      this.participantID = !preview ? response.data.participant.id : 0;
      this.surveyLoadingStatus = LOADING_COMPLETED_SUCCESS;
    },
    sendUserToDonePage() {
      let payload = {
        postPrelimToServer: false,
        sendToAdditionalQuestion: false,
      };
      this.$store.dispatch(USER_POSITION_IN_SURVEY, payload);
    },
    ifMoreQuestionsToAnswer() {
      return this.currentQuestion < this.mediaQuestions.length;
    },
    createObjectToPost(numberSelected) {
      const elapsed = new Date() - this.start;
      const objToPost = {};
      objToPost["rating"] = numberSelected;
      objToPost["responseTime"] = elapsed;
      return objToPost;
    },
    ratingClick(numberSelected) {
      http.post(
        this.$store.getters[GET_BACKEND_URL](
          `surveys/media/${this.mediaQuestions[this.currentQuestion].id}/${this.participantID}`
        ),
        { rating: numberSelected },
        this.$store.getters[GET_HTTP_CONFIG]
      );
      this.$emit("click", numberSelected);
    },
  },
  created() {
    if (this.$store.state.surveyModule.isSurveyPreview) {
      this.setComponentState(this.$store.state.surveyModule.survey);
      this.ifMoreQuestionsToAnswer() ? this.setAnswers() : this.sendUserToDonePage();
    } else {
      http
        .get(
          this.$store.getters[GET_BACKEND_URL](`surveys/${this.$route.params.survey_id}`),
          this.$store.getters[GET_HTTP_CONFIG]
        )
        .then((response) => {
          this.setComponentState(response);
          this.ifMoreQuestionsToAnswer() ? this.setAnswers() : this.sendUserToDonePage();
        })
        .catch((err) => {
          this.surveyLoadingStatus = LOADING_COMPLETED_FAILURE;
        });
    }
  },
  computed: {
    ratings() {
      return this.$t("answerNumbers").slice(0, this.maxRating);
    },
    surveysAreLoading() {
      return this.surveyLoadingStatus === LOADING_STARTED;
    },
    surveysHaveLoaded() {
      return this.surveyLoadingStatus === LOADING_COMPLETED_SUCCESS;
    },
    surveyLoadingError() {
      return this.surveyLoadingStatus === LOADING_COMPLETED_FAILURE;
    },
    surveyNotLoadingMessage() {
      return "For some reason, it looks like the Survey couldnt load";
    },
    surveyNotLoading() {
      return "Server Error";
    },
    getQuestionNumber() {
      return this.ifMoreQuestionsToAnswer() ? this.currentQuestion + 1 : this.mediaQuestions.length;
    },
    indicateDisabled() {
      return {
        "indicate-disabled": this.disableClick,
      };
    },
    leastImportant() {
      return this.ratingQuestions[this.currentQuestion].ratingLow.replace(/&comma;/g, ",");
    },
    mostImportant() {
      return this.ratingQuestions[this.currentQuestion].ratingHigh.replace(/&comma;/g, ",");
    },
    intermediateValues() {
      return this.ratingQuestions[this.currentQuestion].ratingIntermediate;
    },
    ratingLabels() {
      const labels = [];
      labels.push(`1 = ${this.leastImportant}`);
      if (this.intermediateValues) {
        this.intermediateValues.split(",").forEach((value, idx) => {
          if (value.length > 0) {
            labels.push(`${idx + 2} = ${value.replace(/&comma;/g, ",")}`);
          }
        });
      }
      labels.push(`${this.maxRating} = ${this.mostImportant}`);
      return labels;
    },
    getRatingQuestion() {
      return this.ratingQuestions[this.currentQuestion].ratingQuestion.replace(/&comma;/g, ",");
    },
    isProspx() {
      return this.$store.state.surveyModule.survey.data.study.brand === "prospx";
    },
    isGeneralBrandSurvey() {
      return !this.$store.state.surveyModule.survey.data.study.brand;
    },
    totalAmountOfQuestions() {
      return this.mediaQuestions.length;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../scss/main";

.extra-padding {
  padding-top: 5px;
  padding-bottom: 5px;
}

.outer-container {
  padding-top: 5%;
  width: 80%;
  max-width: 1024px;
}

.regular-text {
  font-size: 16px;
}

.primary-color {
  color: $red-2;
}

.bold {
  font-weight: bold;
}

.line-spacing {
  padding-top: 15px;
  padding-bottom: 15px;
}

.each-answer {
  padding-top: 10px;
  padding-bottom: 10px;
}

.label-spacing {
  padding-top: 5px;
  padding-bottom: 5px;
}

.rating-container {
  display: inline-block;
  margin-right: 10px;
  margin-bottom: 10px;
}

.rating-box {
  display: inline-flex;
  width: 50px;
  height: 50px;
  color: $white-1;
  background-color: $cyan-1;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.indicate-disabled {
  /*cursor: wait;*/
  /*opacity: .5;*/
}

.entire-ratings-component {
  display: inline-block;
  position: relative;
  direction: ltr;
}

.answers {
  padding-left: 7%;
}

.least-important {
  display: inline-block;
}

.most-important {
  display: inline-block;
  position: absolute;
  right: 10px;
}
</style>
